import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { useImportHistory } from "./hooks";
import type { ImportHistoryEntry } from "./types";
import { AgreementEditDrawer } from "./drawers/AgreementEditDrawer";

// Formats an ISO timestamp to a locale-friendly short form: "DD.MM.YYYY HH:MM".
function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Compact count badge — colour-coded: green=imported, amber=skipped, red=errors.
function CountBadge({
  value,
  variant,
}: {
  value: number;
  variant: "green" | "amber" | "red" | "grey";
}) {
  const colours: Record<string, string> = {
    green: "bg-green-bg text-green-dark",
    amber: "bg-warning-bg text-warning",
    red: "bg-red-bg text-red",
    grey: "bg-grey-100 text-grey-600",
  };
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[12px] font-medium tabular-nums ${colours[variant]}`}
    >
      {value}
    </span>
  );
}

/**
 * Panel shown on the ModeStep when a merchant is selected.
 * Fetches and displays the merchant's last import runs (capped at 50 by the backend).
 * Runs with created_loans can be expanded to reveal individual agreements (0124.02).
 */
export function ImportHistoryPanel({ merchantId }: { merchantId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useImportHistory(merchantId);

  // Which history entry row is currently expanded (by entry.id).
  const [expandedId, setExpandedId] = useState<ImportHistoryEntry["id"] | null>(null);
  // Which loan is open in the edit drawer (null = drawer closed).
  const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);

  function toggleExpand(entryId: ImportHistoryEntry["id"]) {
    setExpandedId((prev) => (prev === entryId ? null : entryId));
  }

  return (
    <section>
      <h3 className="mb-3 text-[15px] font-semibold text-grey-800">
        {t("import.importHistory.title")}
      </h3>

      {isLoading && (
        <p className="text-[13px] text-grey-400">{t("import.importHistory.loading")}</p>
      )}

      {!isLoading && (!data || data.length === 0) && (
        <p className="rounded-card border border-dashed border-grey-200 px-4 py-6 text-center text-[13px] text-grey-400">
          {t("import.importHistory.empty")}
        </p>
      )}

      {!isLoading && data && data.length > 0 && (
        <div className="overflow-hidden rounded-card border border-grey-200 bg-white">
          {/* Responsive scroll container so the table doesn't break narrow viewports */}
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-grey-100 bg-grey-50 text-left text-[12px] font-medium text-grey-500">
                  <th className="px-3 py-2">{t("import.importHistory.colDate")}</th>
                  <th className="px-3 py-2">{t("import.importHistory.colFilename")}</th>
                  <th className="px-3 py-2 text-center">{t("import.importHistory.colImported")}</th>
                  <th className="px-3 py-2 text-center">{t("import.importHistory.colSkipped")}</th>
                  <th className="px-3 py-2 text-center">{t("import.importHistory.colErrors")}</th>
                  <th className="px-3 py-2 text-center">{t("import.importHistory.colTotal")}</th>
                  <th className="px-3 py-2">{t("import.importHistory.colActor")}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((entry, idx) => {
                  const hasLoans = (entry.created_loans?.length ?? 0) > 0;
                  const isExpanded = expandedId === entry.id;
                  const rowBg = idx % 2 === 0 ? "bg-white" : "bg-grey-50/40";

                  return (
                    // Keyed Fragment: map() returns multiple <tr>; the shorthand <> can't hold a key.
                    <Fragment key={entry.id}>
                      <tr
                        className={`border-b border-grey-100 last:border-0 ${rowBg} ${hasLoans ? "cursor-pointer hover:bg-grey-50" : ""}`}
                        onClick={hasLoans ? () => toggleExpand(entry.id) : undefined}
                        aria-expanded={hasLoans ? isExpanded : undefined}
                      >
                        {/* Timestamp — shows chevron when the row has created_loans */}
                        <td className="whitespace-nowrap px-3 py-2 text-grey-600 tnum">
                          <span className="inline-flex items-center gap-1">
                            {formatTimestamp(entry.timestamp)}
                            {hasLoans && (
                              <ChevronDown
                                size={14}
                                className={`shrink-0 text-grey-400 transition-transform duration-150 ${isExpanded ? "rotate-180" : "rotate-0"}`}
                              />
                            )}
                          </span>
                        </td>
                        {/* Filename */}
                        <td className="max-w-[180px] truncate px-3 py-2 text-grey-700" title={entry.filename ?? undefined}>
                          {entry.filename ?? t("import.importHistory.noFile")}
                        </td>
                        {/* Imported */}
                        <td className="px-3 py-2 text-center">
                          <CountBadge value={entry.imported} variant="green" />
                        </td>
                        {/* Skipped */}
                        <td className="px-3 py-2 text-center">
                          <CountBadge value={entry.skipped_exists} variant="amber" />
                        </td>
                        {/* Errors */}
                        <td className="px-3 py-2 text-center">
                          <CountBadge value={entry.error_count} variant={entry.error_count > 0 ? "red" : "grey"} />
                        </td>
                        {/* Total */}
                        <td className="px-3 py-2 text-center">
                          <CountBadge value={entry.total} variant="grey" />
                        </td>
                        {/* Actor */}
                        <td className="max-w-[140px] truncate px-3 py-2 text-grey-500" title={entry.actor_repr}>
                          {entry.actor_repr}
                        </td>
                      </tr>

                      {/* Expanded sub-row: list of created agreements */}
                      {isExpanded && hasLoans && (
                        <tr className="border-b border-grey-100 last:border-0 bg-grey-50/60">
                          <td colSpan={7} className="px-4 py-2">
                            <div className="flex flex-col divide-y divide-grey-100">
                              {entry.created_loans!.map((ref) => (
                                <button
                                  key={ref.loan_id}
                                  type="button"
                                  onClick={(e) => {
                                    // Prevent the row click from toggling expand.
                                    e.stopPropagation();
                                    setSelectedLoanId(ref.loan_id);
                                  }}
                                  className="flex items-center gap-3 py-1.5 text-left text-[13px] hover:bg-grey-100/60 rounded px-1 transition-colors"
                                >
                                  {/* Client name */}
                                  <span className="font-medium text-grey-800 min-w-0 truncate flex-1">
                                    {ref.client_label}
                                  </span>
                                  {/* Loan type label — reusing import.loanType keys */}
                                  <span className="shrink-0 text-[12px] text-grey-500">
                                    {t(`import.loanType.${ref.type}`, { defaultValue: ref.type })}
                                  </span>
                                  {/* Agreement code */}
                                  <span className="shrink-0 font-mono text-[12px] text-grey-500">
                                    {ref.agreement_code}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Legacy / skip-only runs (no created_loans) stay plain — no chevron, not
                          clickable. We deliberately render no extra hint row: production history is
                          mostly legacy file-imports, and a note under every one would be noise. */}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Agreement edit drawer — opens when an agreement item is clicked */}
      {selectedLoanId !== null && (
        <AgreementEditDrawer
          loanId={selectedLoanId}
          onClose={() => setSelectedLoanId(null)}
          onSaved={() => setSelectedLoanId(null)}
        />
      )}
    </section>
  );
}
